import { ProfileFieldValue } from "../api/types/crm/profile-fields";

export interface TagItem {
  id: string;
  label: string;
}

export interface TagWithFieldValues {
  tag: TagItem;
  fieldValues: ProfileFieldValue[];
}

export interface TagWithFieldValueIds {
  id: string;
  label: string;
  fieldValueIds: string[];
}

/**
 * Parses a tag value that may be a string (JSON) or already an array.
 * Returns an array of tag items, or empty array if parsing fails.
 */
export function parseTagValue(value: unknown): TagItem[] {
  let parsed = value;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  // Filter to ensure we have valid tag objects
  return parsed.filter(
    (item): item is TagItem =>
      typeof item === "object" &&
      item !== null &&
      typeof item.id === "string" &&
      typeof item.label === "string",
  );
}

/**
 * Builds a map of tag ID to tag info and associated field values.
 * Used when you need the full ProfileFieldValue objects.
 */
export function buildTagToFieldValuesMap(
  tagFieldValues: ProfileFieldValue[],
): Map<string, TagWithFieldValues> {
  const tagMap = new Map<string, TagWithFieldValues>();

  tagFieldValues.forEach((tagFv) => {
    const tags = parseTagValue(tagFv.value);

    tags.forEach((tag) => {
      if (!tagMap.has(tag.id)) {
        tagMap.set(tag.id, {
          tag,
          fieldValues: [],
        });
      }

      const entry = tagMap.get(tag.id)!;
      // Deduplicate - only add if not already present
      if (!entry.fieldValues.some((fv) => fv.id === tagFv.id)) {
        entry.fieldValues.push(tagFv);
      }
    });
  });

  return tagMap;
}

/**
 * Builds a list of unique tags with their associated field value IDs.
 * Used when you only need the IDs for updates/mutations.
 */
export function buildTagsWithFieldValueIds(
  tagFieldValues: ProfileFieldValue[],
): TagWithFieldValueIds[] {
  const tagMap = new Map<string, { tag: TagItem; ids: string[] }>();

  tagFieldValues.forEach((tagFv) => {
    const tags = parseTagValue(tagFv.value);

    tags.forEach((tag) => {
      if (!tagMap.has(tag.id)) {
        tagMap.set(tag.id, { tag, ids: [] });
      }

      const entry = tagMap.get(tag.id)!;
      // Deduplicate
      if (!entry.ids.includes(tagFv.id)) {
        entry.ids.push(tagFv.id);
      }
    });
  });

  return Array.from(tagMap.values()).map(({ tag, ids }) => ({
    id: tag.id,
    label: tag.label,
    fieldValueIds: ids,
  }));
}

/**
 * Checks if a field value contains valid tag data (non-empty array).
 */
export function hasValidTagData(fieldValue: ProfileFieldValue): boolean {
  const tags = parseTagValue(fieldValue.value);
  return tags.length > 0;
}
